import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { z } from 'zod';
import { UsersTable } from '@/components/user/UsersTable';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type {
    InfiniteScrollListCursor,
    InfiniteScrollListFieldPredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type {
    Role,
    SearchFieldPredicate,
    SearchJsonPathPredicate,
    UserProfileDetailedPublicResponse,
    UserProfileDetailedResponse,
} from '@anchorbase/pillar-sdk';
import { RoleSchema } from '@anchorbase/pillar-sdk';
import { Status } from '@anchorbase/pillar-sdk';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';

type RoleLabelMap = Partial<Record<Role, string>>;

interface RoleEntry {
    readonly value: Role;
    readonly label: string;
}

interface UsersListPageProps<
    TUser extends SupportedUser,
    TCursor extends InfiniteScrollListCursor,
> {
    title: string;
    cardTitle: string;
    createPath?: string;
    createButtonLabel?: string;
    roles: RoleLabelMap;
    instanceName?: string;
    queryKeyPrefix: ReadonlyArray<string>;
    fetchPage: (params: {
        limit?: number;
        cursor?: TCursor;
        terms?: string[];
        field_predicates?: SearchFieldPredicate[];
        json_path_predicates?: SearchJsonPathPredicate[];
        show_deleted?: boolean;
    }) => Promise<{
        users: ReadonlyArray<TUser>;
        pagination: {
            has_more: boolean;
            next_cursor?: TCursor;
        };
    }>;
    parseCursorParam?: (pageParam: unknown) => TCursor | undefined;
}

interface UsersListPageNumberCursorProps<TUser extends SupportedUser>
    extends UsersListPageProps<TUser, number> {
    parseCursorParam?: undefined;
}

interface UsersListPageStringCursorProps<TUser extends SupportedUser>
    extends UsersListPageProps<TUser, string> {
    parseCursorParam: (pageParam: unknown) => string | undefined;
}

type SupportedUser = UserProfileDetailedResponse | UserProfileDetailedPublicResponse;
type SupportedUsersListPageProps<TUser extends SupportedUser> =
    | UsersListPageNumberCursorProps<TUser>
    | UsersListPageStringCursorProps<TUser>;

const UserStatusPredicateOptions: readonly { readonly value: Status; readonly label: string }[] =
    [
        { value: Status.ACTIVE, label: 'Active' },
        { value: Status.PENDING, label: 'Pending' },
        { value: Status.DISABLED, label: 'Disabled' },
        { value: Status.DELETED, label: 'Deleted' },
    ];

const UserMfaPredicateOptions: readonly {
    readonly value: boolean;
    readonly label: string;
}[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
];

function getRoleAssignments(
    user: SupportedUser
): ReadonlyArray<
    NonNullable<SupportedUser['default_role_assignment']> & { isDefault: boolean }
> {
    const assignments: Array<
        NonNullable<SupportedUser['default_role_assignment']> & { isDefault: boolean }
    > = [];
    if (user.default_role_assignment !== null) {
        assignments.push({
            ...user.default_role_assignment,
            isDefault: true,
        });
    }
    for (const roleAssignment of user.other_role_assignments) {
        assignments.push({
            ...roleAssignment,
            isDefault: false,
        });
    }
    return assignments;
}

function parseUsersListNumericCursor(pageParam: unknown): number | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = z.number().safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected numeric cursor');
    }
    return parseResult.data;
}

function renderUsersListItems<TUser extends SupportedUser>(
    items: ReadonlyArray<TUser>,
    showDeleted: boolean,
    statusFilter: string,
    roleFilter: string
): ReactElement {
    const filteredUsers: ReadonlyArray<TUser> = items.filter((user) => {
        if (showDeleted) {
            if (user.status !== 'DELETED') {
                return false;
            }
        } else if (user.status === 'DELETED') {
            return false;
        }

        const matchesStatus: boolean = showDeleted
            ? true
            : statusFilter === 'all' || user.status === statusFilter;
        const matchesRole: boolean =
            roleFilter === 'all' ||
            getRoleAssignments(user).some(
                (roleAssignment) => roleAssignment.role === roleFilter
            );
        return matchesStatus && matchesRole;
    });

    if (filteredUsers.length === 0) {
        return <p className="text-sm text-gray-600">No users found.</p>;
    }

    return <UsersTable users={filteredUsers} />;
}

export function UsersListPage<
    TUser extends SupportedUser = UserProfileDetailedResponse,
    _TCursor extends number = number,
>(
    props: UsersListPageNumberCursorProps<TUser>
): ReactElement;
export function UsersListPage<
    TUser extends SupportedUser = UserProfileDetailedResponse,
    _TCursor extends string = string,
>(
    props: UsersListPageStringCursorProps<TUser>
): ReactElement;
export function UsersListPage<TUser extends SupportedUser = UserProfileDetailedResponse>(
    props: SupportedUsersListPageProps<TUser>
): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const userListsVersion: number = useDataInvalidationStore((state) => state.userListsVersion);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            ...props.queryKeyPrefix,
            `users=${userListsVersion}`,
            `showDeleted=${showDeleted}`],
        [props.queryKeyPrefix, showDeleted, userListsVersion]
    );
    const roleEntries: RoleEntry[] = useMemo(() => {
        const resolvedRoleEntries: RoleEntry[] = [];
        for (const roleKey in props.roles) {
            if (!Object.prototype.hasOwnProperty.call(props.roles, roleKey)) {
                continue;
            }
            const roleParseResult = RoleSchema.safeParse(roleKey);
            if (!roleParseResult.success) {
                throw new Error(`UsersListPage: Invalid role key "${roleKey}"`);
            }
            const roleValue: Role = roleParseResult.data;
            const label = props.roles[roleValue];
            if (label !== undefined) {
                resolvedRoleEntries.push({ value: roleValue, label });
            }
        }
        return resolvedRoleEntries;
    }, [props.roles]);
    const predicateControls = useMemo(
        () => ({
            fieldPredicates: [
                {
                    field: 'first_name',
                    label: 'Name',
                    operator: 'eq',
                    valueType: 'string',
                },
                {
                    field: 'last_name',
                    label: 'Last Name',
                    operator: 'eq',
                    valueType: 'string',
                },
                {
                    field: 'primary_email',
                    label: 'Primary Email',
                    operator: 'eq',
                    valueType: 'string',
                },
                {
                    field: 'organization_name',
                    label: 'Organization',
                    operator: 'eq',
                    valueType: 'string',
                },
                {
                    field: 'role',
                    label: 'Role',
                    operator: 'in',
                    valueType: 'string',
                    options: roleEntries.map((entry) => ({
                        value: entry.value,
                        label: entry.label,
                    })),
                },
                {
                    field: 'status',
                    label: 'Status',
                    operator: 'in',
                    valueType: 'string',
                    options: UserStatusPredicateOptions,
                },
                {
                    field: 'mfa_configured',
                    label: 'MFA',
                    operator: 'eq',
                    valueType: 'boolean',
                    options: UserMfaPredicateOptions,
                },
            ] satisfies ReadonlyArray<InfiniteScrollListFieldPredicateDescriptor>,
        }),
        [roleEntries]
    );
    const shouldShowRoleFilter: boolean = roleEntries.length > 1;
    const headerActions =
        props.createPath !== undefined && props.createButtonLabel !== undefined ? (
            <Button
                onClick={() =>
                    breadcrumbNavigate({
                        source: 'panel',
                        target: {
                            url: props.createPath!,
                            title: props.createButtonLabel!,
                        },
                    })
                }
            >
                {props.createButtonLabel}
            </Button>
        ) : undefined;
    const controls = (
        <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter} disabled={showDeleted}>
                <SelectTrigger
                    className={`min-w-[140px] w-auto ${showDeleted ? 'opacity-50' : ''}`}
                >
                    <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="DISABLED">Disabled</SelectItem>
                </SelectContent>
            </Select>
            {shouldShowRoleFilter ? (
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        {roleEntries.map((entry) => (
                            <SelectItem key={entry.value} value={entry.value}>
                                {entry.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : null}
            <div className="flex items-center gap-2">
                <Label htmlFor="show-deleted" className="cursor-pointer">
                    Show deleted
                </Label>
                <Checkbox
                    id="show-deleted"
                    checked={showDeleted}
                    onCheckedChange={(checked) => {
                        setShowDeleted(checked === true);
                        if (checked === true) {
                            setStatusFilter('all');
                        }
                    }}
                />
            </div>
        </div>
    );

    if (props.parseCursorParam !== undefined) {
        return (
            <InfiniteScrollList<
                TUser,
                string,
                ReadonlyArray<string>
            >
                pageTitle={props.title}
                cardTitle={props.cardTitle}
                queryPlaceholder="Search..."
                queryKeyPrefix={queryKeyPrefix}
                pageSize={100}
                fetchPage={async (params) => {
                    const response = await props.fetchPage({
                        limit: params.limit,
                        cursor: params.cursor,
                        terms: params.terms === undefined ? undefined : [...params.terms],
                        ...(params.fieldPredicates === undefined
                            ? {}
                            : { field_predicates: [...params.fieldPredicates] }),
                        ...(params.jsonPathPredicates === undefined
                            ? {}
                            : { json_path_predicates: [...params.jsonPathPredicates] }),
                        show_deleted: showDeleted ? true : undefined,
                    });
                    return { items: response.users, pagination: response.pagination };
                }}
                parseCursorParam={props.parseCursorParam}
                stateCopy={{
                    loadingLabel: 'Loading users...',
                    emptyLabel: 'No users found.',
                    endOfListLabel: 'End of users.',
                    genericErrorLabel: 'Failed to load users.',
                }}
                headerActions={headerActions}
                controls={controls}
                predicateControls={predicateControls}
                renderItems={({ items }) =>
                    renderUsersListItems(items, showDeleted, statusFilter, roleFilter)
                }
            />
        );
    }

    return (
        <InfiniteScrollList<
            TUser,
            number,
            ReadonlyArray<string>
        >
            pageTitle={props.title}
            cardTitle={props.cardTitle}
            queryPlaceholder="Search..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            fetchPage={async (params) => {
                const response = await props.fetchPage({
                    limit: params.limit,
                    cursor: params.cursor,
                    terms: params.terms === undefined ? undefined : [...params.terms],
                    ...(params.fieldPredicates === undefined
                        ? {}
                        : { field_predicates: [...params.fieldPredicates] }),
                    ...(params.jsonPathPredicates === undefined
                        ? {}
                        : { json_path_predicates: [...params.jsonPathPredicates] }),
                    show_deleted: showDeleted ? true : undefined,
                });
                return { items: response.users, pagination: response.pagination };
            }}
            parseCursorParam={parseUsersListNumericCursor}
            stateCopy={{
                loadingLabel: 'Loading users...',
                emptyLabel: 'No users found.',
                endOfListLabel: 'End of users.',
                genericErrorLabel: 'Failed to load users.',
            }}
            headerActions={headerActions}
            controls={controls}
            predicateControls={predicateControls}
            renderItems={({ items }) =>
                renderUsersListItems(items, showDeleted, statusFilter, roleFilter)
            }
        />
    );
}
